import React from 'react'
import { useIntl } from 'react-intl'
import Modal from 'LuxKit/Modal/Modal'
import styled from 'styled-components'
import { rem } from 'polished'
import BodyText from 'LuxKit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { INTL_DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import Hr from 'components/Common/Hr'
import HelpPane from 'components/Common/HelpPane'
import FlexibleCancellationCharterFlightFees from './FlexibleCancellationCharterFlightFees'

const ModalContent = styled.div`
  padding-top: ${rem(16)};
`

const CancellationTermsWrapper = styled.div`
  display: grid;
  grid-column-gap: ${rem(18)};
  margin-top: ${rem(40)};

  ${mediaQueryUp.tablet} {
    grid-template-rows: 1fr;
    grid-template-columns: 1fr;
  }
`

const TermsGrid = styled.div`
  display: grid;
  grid-column-gap: ${rem(16)};
  grid-row-gap: ${rem(18)};
  grid-template-columns: 1fr 2fr;

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr;
  }
`

interface Props {
  checkInDate?: string | moment.Moment
  startBasedOnToday: moment.Moment
  startBasedOnCheckIn: moment.Moment | undefined
  cancellationAfterChangeOfMindPeriod: boolean
  days: number
}

function OfferPropertyFlexibleCancellationCharterPolicyModal(props: Props) {
  const {
    checkInDate,
    startBasedOnToday,
    cancellationAfterChangeOfMindPeriod,
    startBasedOnCheckIn,
    days,
  } = props

  const intl = useIntl()
  const intlDateFormatter = useIntlDateFormatter()

  const refundDescription = !checkInDate
    ? intl.formatMessage({
        id: 'c8Ck6rCAw5UFbPa6OyQK',
        defaultMessage: '100% refund of total purchase price to your original payment method or credit (provided cancellation is made at least 14 days prior to departure)',
        description: 'Refund description for charter flexible cancellation policy when no check-in date is set',
      })
    : intl.formatMessage({
        id: 'AtKRcO3cNxAIlIN6bFnP',
        defaultMessage: '100% refund of total purchase price to your original payment method or credit',
        description: 'Refund description for charter flexible cancellation policy',
      })

  return <Modal
    title={intl.formatMessage({
      id: 'ip9FOw9dQ_KIKaoEbQDw',
      defaultMessage: 'Cancellation policy',
      description: 'Title for flexible cancellation policy modal in the cancellation flow',
    })}
    size="L"
  >
    <ModalContent>
      <BodyText variant="medium" as="p">
        {intl.formatMessage({
          id: 'm7DkNdGSUKcfWGfNhqQf',
          defaultMessage: 'Things don’t always work out, so enjoy peace of mind knowing that the following cancellation options are available to you.',
          description: 'Introductory text for flexible cancellation policy modal',
        })}
      </BodyText>
      <CancellationTermsWrapper>
        <TermsGrid>
          {!checkInDate && <BodyText variant="medium" weight="bold" as="span">
            {intl.formatMessage({
              id: 'rQ_eM2Pk1se-zc91qLAN',
              defaultMessage: 'Up to 7 days after purchase',
              description: 'Cancellation period label for up to 7 days after purchase',
            })}
          </BodyText>}
          {checkInDate && <BodyText variant="medium" weight="bold" as="span">
            {intl.formatMessage({
              id: 'BapN2ha2DX0xHbCKAdTH',
              defaultMessage: 'Cancel until {date}',
              description: 'Cancellation deadline label with date in the date selection or calendar picker',
            }, {
              date: intlDateFormatter(startBasedOnToday, INTL_DMY_CASUAL_SHORT_FORMAT),
            })}
          </BodyText>}
          <BodyText variant="medium" as="span">
            {refundDescription}
          </BodyText>
        </TermsGrid>
        <Hr size="md" />
        {cancellationAfterChangeOfMindPeriod && startBasedOnCheckIn && <>
          <TermsGrid>
            <BodyText variant="medium" weight="bold" as="span">
              {intl.formatMessage({
                id: 'vV6_tOpe4mjOu20-GSSX',
                defaultMessage: 'Cancel until {date}',
                description: 'Cancellation deadline label with check-in based date',
              }, {
                date: intlDateFormatter(startBasedOnCheckIn, INTL_DMY_CASUAL_SHORT_FORMAT),
              })}
            </BodyText>
            <FlexibleCancellationCharterFlightFees />
          </TermsGrid>
          <Hr size="md" />
        </>}
        {!cancellationAfterChangeOfMindPeriod && !checkInDate && <>
          <TermsGrid>
            <BodyText variant="medium" weight="bold" as="span">
              {intl.formatMessage({
                id: 'pB_f5eGN22C41IgcH3ok',
                defaultMessage: 'Up to {days} days prior to departure',
                description: 'Charter cancellation period label with days count in a modal dialog',
              }, {
                days: days + 1,
              })}
            </BodyText>
            <FlexibleCancellationCharterFlightFees />
          </TermsGrid>
          <Hr size="md" />
        </>}
        <TermsGrid>
          {!checkInDate && <BodyText variant="medium" weight="bold" as="span">
            {intl.formatMessage({
              id: 'kwsH34KRG8uewlnydKKl',
              defaultMessage: 'From {days} days until departure',
              description: 'Charter non-refundable period label with days count',
            }, {
              days,
            })}
          </BodyText>}
          {checkInDate && startBasedOnCheckIn && <BodyText variant="medium" weight="bold" as="span">
            {intl.formatMessage({
              id: 'tSjnUereVciV4T59Pcm0',
              defaultMessage: 'Cancel after {date}',
              description: 'Non-refundable period label with date in the date selection or calendar picker',
            }, {
              date: intlDateFormatter(startBasedOnCheckIn, INTL_DMY_CASUAL_SHORT_FORMAT),
            })}
          </BodyText>}
          <BodyText variant="medium" as="span">
            {intl.formatMessage({
              id: 'R2W5ioEVuvyt2b2l4I-9',
              defaultMessage: 'Non-refundable',
              description: 'Label for non-refundable cancellation period in the cancellation flow',
            })}
          </BodyText>
        </TermsGrid>
      </CancellationTermsWrapper>
      <HelpPane />
    </ModalContent>
  </Modal>
}

export default OfferPropertyFlexibleCancellationCharterPolicyModal
